import req from '@sportninja/common/api/request'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import React from 'react'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import { registerError } from 'src/utils/sentry'
import { PAYMENT_PROVIDERS } from './usePaymentProvider'

const SportsPayFileName = __DEV__
  ? 'interpay_embed_test.min.js'
  : 'sportninja_v1.0.min.js'

const PAYMENT_STATUS = {
  SUCCESS: 'success',
  FAILED: 'failed',
  PENDING: 'pending',
  PROCESSING: 'processing',
}

/**
 * Loads the SportsPay script dynamically
 * @param {string} scriptUrl - The URL of the SportsPay script to load
 * @returns {Promise<void>} A promise that resolves when the script is loaded
 */
async function loadSportsPayScript(scriptUrl) {
  if (!scriptUrl) {
    throw new Error('Script URL is required to load SportsPay script')
  }

  // Check if script is already loaded
  if (document.querySelector(`script[src="${scriptUrl}"]`)) {
    return
  }

  console.log('### Loading SportsPay Script', scriptUrl)

  const script = document.createElement('script')
  script.src = scriptUrl
  script.async = true

  // Create a promise to wait for script to load
  const scriptPromise = new Promise((resolve, reject) => {
    script.onload = resolve
    script.onerror = () => reject(new Error('Failed to load SportsPay script'))
  })

  console.log('### Adding SportsPay Script to Head')

  document.head.appendChild(script)
  await scriptPromise

  console.log('### SportsPay Script Loaded')
}

async function createPaymentIntent({ rootScheduleId, billingId, currencyId }) {
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/intent`,
    {
      method: 'POST',
      body: JSON.stringify({
        billing_schedule_id: billingId,
        currency_id: currencyId,
      }),
    }
  )
  return clientSecret
}

/**
 * Charges a payment for a registration, from the admin dashboard
 * @param {Object} params - The parameters for the chargePayment function
 * @param {string} params.rootScheduleId - The root schedule ID
 * @param {string} params.billingId - The billing ID
 * @returns {Promise<string>} The response from the API call
 * @throws {Error} If no Root Schedule ID or Billing ID is provided
 */
export async function chargePayment({ rootScheduleId, billingId }) {
  if (!rootScheduleId || !billingId) {
    throw new Error('No Root Schedule ID or Billing ID Provided')
  }
  const clientSecret = await req(
    `/registration/${rootScheduleId}/payment/charge`,
    {
      method: 'POST',
      body: JSON.stringify({
        billing_schedule_id: billingId,
      }),
    }
  )
  return clientSecret
}

/**
 * Charges a payment for a registration, coming from the home screen PAYMENT BANNER
 * @param {string} rootScheduleId - The root schedule ID
 * @param {string} billingId - The billing ID
 * @param {string} paymentProviderToken - The payment provider token
 * @returns {Promise<string>} The response from the API call
 * @throws {Error} If no Billing ID or Payment Provider Token is provided
 */
export async function chargeUserPayment({ billingId, paymentProviderToken }) {
  if (!billingId || !paymentProviderToken) {
    throw new Error('No Billing ID or Payment Provider Token Provided')
  }
  const clientSecret = await req(`/users/me/billing/${billingId}/charge`, {
    method: 'POST',
    body: JSON.stringify({
      payment_provider_token: paymentProviderToken,
    }),
  })
  return clientSecret
}

async function getDivision({ billingId }) {
  if (!billingId) {
    throw new Error('No Billing Id Provided')
  }

  const response = await req(`/users/me/billing/${billingId}`)
  const data = response?.data || response

  // Return the billing schedule object directly for consumers
  return data
}

const useSinglePaymentProvider = ({ billingId }) => {
  console.log('### Billing Id', billingId)

  const [state, setState] = useState({
    secret: null,
    billingSchedule: null,
    loading: false,
    error: null,
    paymentIsLoading: false,
    paymentStatus: null,
    paymentError: null,
    bootstrapFinished: false,
    sportsPayToken: null,
    paymentProvider: null,
    sportsPayHost: null,
  })

  const bootstrapCalled = React.useRef(false)

  /**
   * Effect hook that fetches billing schedule data and creates a payment intent when component mounts
   * or when any of the ID dependencies change.
   *
   * The flow:
   * 1. Validates that at least one ID is provided
   * 2. Sets loading state to true
   * 3. Fetches billing schedule data using the provided ID
   * 4. If no billing schedule is found, sets an error state
   * 5. Creates a payment intent using billing schedule data
   * 6. If no secret is generated, sets an error state
   * 7. Updates state with the fetched data and sets loading to false
   */
  useEffect(() => {
    const fetchDivisionAndCreatePaymentIntent = async () => {
      try {
        // Set loading state and clear error
        setState((prev) => ({
          ...prev,
          loading: true,
          error: null,
          paymentIsLoading: false,
          paymentStatus: null,
          paymentError: null,
        }))

        // Fetch billing schedule data
        const billingSchedule = await getDivision({ billingId })
        if (!billingSchedule) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'No Division Found',
          }))
          return
        }

        // Get payment provider from billing schedule data
        const paymentProvider =
          billingSchedule?.payment_provider_type?.id ||
          PAYMENT_PROVIDERS.SPORTS_PAY

        // Create payment intent using billing schedule data
        const { client_secret, host_name } = await createPaymentIntent({
          rootScheduleId: billingSchedule?.root_schedule_id,
          billingId: billingSchedule?.uid,
          currencyId: billingSchedule?.currency?.id,
        })
        if (!client_secret) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: 'No Secret Found',
          }))
          return
        }

        // Update state with successful data
        setState((prev) => ({
          ...prev,
          secret: client_secret,
          sportsPayHost: host_name,
          billingSchedule,
          loading: false,
          paymentIsLoading: false,
          paymentStatus: null,
          errorMessage: null,
          paymentProvider,
        }))
      } catch (e) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e,
        }))
      }
    }

    // Exit early if no IDs are provided
    if (!billingId) {
      return
    }

    fetchDivisionAndCreatePaymentIntent()
  }, [billingId])

  /**
   * Initializes the payment provider (Stripe or SportsPay) and sets up the payment form submission handler.
   *
   * The flow:
   * 1. Sets loading state to true and clears any previous errors
   * 2. Validates that required payment data exists
   * 3. Initializes the appropriate payment client based on provider
   * 4. Creates and mounts the payment element to the DOM
   * 5. Sets up an event listener for the payment form submission
   *
   * @returns {void}
   */
  useEffect(() => {
    // Only run bootstrap if we have the necessary data and haven't bootstrapped yet
    if (
      !state?.bootstrapFinished &&
      state?.secret &&
      state?.billingSchedule &&
      !bootstrapCalled.current
    ) {
      console.log('### Bootstrap Called')
      bootstrapCalled.current = true

      // Define the bootstrap function inside the effect to avoid dependency issues
      const runBootstrap = async () => {
        console.log('### Run Bootstrap')
        try {
          setState((prev) => ({
            ...prev,
            loading: true,
            error: null,
            bootstrapFinished: false,
          }))

          if (!state?.secret || !state?.billingSchedule) {
            return
          }

          console.log('### Valid secret and billingSchedule', state)

          if (state.paymentProvider === PAYMENT_PROVIDERS.STRIPE) {
            console.log('### Stripe:: Payment Provider')
            let stripeClient = null
            const host = window.location.hostname || window.location.host

            if (
              __DEV__ ||
              !['app.sportninja.com', 'canlanstats.sportninja.com'].includes(
                host
              )
            ) {
              console.log('### Stripe:: Dev Key')
              stripeClient = await loadStripe(
                process.env.REACT_APP_STRIPE_KEY_DEV,
                {
                  stripeAccount: state?.billingSchedule?.connected_account,
                }
              )
            } else {
              console.log('### Stripe:: Prod Key')
              stripeClient = await loadStripe(
                process.env.REACT_APP_STRIPE_KEY,
                {
                  stripeAccount: state?.billingSchedule?.connected_account,
                }
              )
            }

            console.log('### Stripe:: Client', stripeClient)

            if (!stripeClient) {
              console.log('### No Stripe Client Found')
              setState((prev) => ({
                ...prev,
                loading: false,
                error: 'No Stripe Client Found',
              }))
              return
            }

            console.log('### Stripe Elements')

            const elements = stripeClient.elements({
              clientSecret: state?.secret,
              appearance: {
                theme: 'night',
              },
            })

            console.log('### Stripe Elements', elements)

            let paymentElement = elements.create('payment', {
              paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            })

            console.log('### Payment Element', paymentElement)

            const paymentDiv = document.querySelector('#payment-element')

            console.log('### Payment Div', paymentDiv)

            if (!paymentDiv) {
              console.log('### No Payment Div Found')
              setState((prev) => ({
                ...prev,
                loading: false,
                error: 'No Payment Element Found',
              }))
              return
            }

            console.log('### Mounting Payment Element')

            paymentElement.mount('#payment-element')

            console.log('### Mounted Payment Element')

            const stripeForm = document.querySelector('#payment-form')

            stripeForm.addEventListener('submit', async (e) => {
              e.preventDefault()

              setState((prev) => ({
                ...prev,
                paymentIsLoading: true,
                paymentStatus: null,
                paymentError: null,
              }))

              if (!stripeClient) {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: null,
                  paymentError: 'No Stripe Client Found',
                }))
                return
              }

              const { error } = await stripeClient.confirmPayment({
                elements,
                redirect: 'if_required',
              })

              if (error) {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: PAYMENT_STATUS.FAILED,
                  paymentError: error.message,
                }))
              } else {
                setState((prev) => ({
                  ...prev,
                  paymentIsLoading: false,
                  paymentStatus: PAYMENT_STATUS.SUCCESS,
                }))
              }
            })
          } else if (state.paymentProvider === PAYMENT_PROVIDERS.SPORTS_PAY) {
            // Initialize SportsPay
            console.log('[SportsPay] Starting initialization')
            try {
              // Load SportsPay JS library
              const scriptUrl = `${state?.sportsPayHost}assets/${SportsPayFileName}`
              console.log('[SportsPay] Loading script from:', scriptUrl)
              await loadSportsPayScript(scriptUrl)
              console.log('[SportsPay] Script loaded successfully')

              // Only update state if component is still mounted
              setState((prev) => ({
                ...prev,
                loading: false,
                error: null,
                bootstrapFinished: true,
              }))
            } catch (e) {
              const errorMessage = getErrorMessage(e)
              console.error(
                '[SportsPay] Initialization failed:',
                errorMessage,
                e
              )
              setState((prev) => ({
                ...prev,
                loading: false,
                error: errorMessage,
                bootstrapFinished: true,
              }))
            }
          }

          setState((prev) => ({
            ...prev,
            loading: false,
            error: null,
            bootstrapFinished: true,
          }))
        } catch (e) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: e,
          }))
        }
      }

      runBootstrap()
    }
  }, [
    state?.bootstrapFinished,
    state?.secret,
    state?.billingSchedule,
    state.paymentProvider,
    state?.sportsPayHost,
    state,
  ])

  /**
   * Validates credit card data with SportsPay
   * @param {Object} cardData - The credit card data to validate
   * @returns {Object} The validation result
   */
  const validateSportsPayCard = useCallback(
    (cardData) => {
      console.log('[SportsPay] Validating card data', cardData)
      console.log('[SportsPay] SportsPay API Key', state?.secret)
      try {
        // eslint-disable-next-line no-undef
        const interpay = new InterPay()
        return interpay.validate(state?.secret, cardData)
      } catch (e) {
        const errorMessage = getErrorMessage(e)
        setState((prev) => ({
          ...prev,
          error: errorMessage,
        }))
        return { datavalid: false, text: errorMessage }
      }
    },
    [state?.secret]
  )

  /**
   * Tokenizes credit card data with SportsPay
   * @param {Object} cardData - The credit card data to tokenize
   * @returns {Promise<Object>} The tokenization result
   */
  const tokenizeSportsPayCard = useCallback(
    async (cardData) => {
      console.log('[SportsPay] Starting card tokenization process')
      try {
        // eslint-disable-next-line no-undef
        const interpay = new InterPay()
        console.log(
          '[SportsPay] Calling tokenize with host:',
          state?.sportsPayHost
        )
        const result = await interpay.tokenize(
          state?.sportsPayHost.replace(/\/$/, ''),
          state?.secret,
          cardData
        )
        console.log(
          '[SportsPay] Tokenization completed successfully',
          result.datavalid ? 'Valid token received' : 'Invalid token received'
        )
        return result
      } catch (e) {
        console.error('[SportsPay] Tokenization failed with error:', e)
        throw e
      }
    },
    [state?.sportsPayHost, state?.secret]
  )

  /**
   * Process payment with SportsPay
   * @param {Object} cardData - Credit card data to process
   * @returns {Promise<boolean>} Whether the payment was successful
   */
  const processSportsPayPayment = useCallback(
    async (cardData) => {
      console.log(
        '[SportsPay] Starting payment processing',
        state.bootstrapFinished
      )

      if (!state.bootstrapFinished) {
        console.error('[SportsPay] Not initialized')
        setState((prev) => ({
          ...prev,
          error: 'Sportspay Is Not Initialized',
        }))
        return false
      }

      setState((prev) => ({
        ...prev,
        paymentIsLoading: true,
        paymentStatus: PAYMENT_STATUS.PROCESSING,
      }))
      console.log('[SportsPay] Payment status set to PROCESSING')

      try {
        // First validate the card data
        console.log('[SportsPay] Validating card data')
        const validationResult = validateSportsPayCard(cardData)
        console.log('[SportsPay] Validation result:', validationResult)

        if (!validationResult.datavalid) {
          console.error(
            '[SportsPay] Card validation failed:',
            validationResult.text
          )
          throw new Error(validationResult.text || 'Invalid Card Data')
        }

        // Then tokenize the card
        console.log('[SportsPay] Tokenizing card')
        const tokenResult = await tokenizeSportsPayCard(cardData)
        console.log('[SportsPay] Tokenization result:', tokenResult)

        if (!tokenResult.datavalid || !tokenResult.ott) {
          console.error('[SportsPay] Tokenization failed:', tokenResult.text)
          throw new Error(tokenResult.text || 'Failed To Tokenize Card')
        }

        console.log('[SportsPay] Token received successfully')

        console.log('[SportsPay] Charging payment - this is the user payment')
        await chargeUserPayment({
          billingId: state?.billingSchedule?.uid || billingId,
          paymentProviderToken: tokenResult.ott,
        })
        console.log('[SportsPay] Payment charged successfully')

        // Store the token for later use

        setState((prev) => ({
          ...prev,
          sportsPayToken: tokenResult.ott,
          paymentStatus: PAYMENT_STATUS.SUCCESS,
          paymentIsLoading: false,
        }))

        return true
      } catch (e) {
        console.error('[SportsPay] Payment processing error:', e)

        const errorMessage = getErrorMessage(e)
        console.error('[SportsPay] Setting error state:', errorMessage)
        setState((prev) => ({
          ...prev,
          error: errorMessage,
          paymentStatus: PAYMENT_STATUS.FAILED,
          paymentIsLoading: false,
        }))

        registerError(e, 'Error processing SportsPay payment', {
          provider: state.paymentProvider,
        })

        return false
      }
    },
    [
      state.bootstrapFinished,
      state?.billingSchedule?.uid,
      state.paymentProvider,
      validateSportsPayCard,
      tokenizeSportsPayCard,
      billingId,
    ]
  )

  /**
   * Resets the bootstrap state to allow for retrying the bootstrap process.
   *
   * This function sets the bootstrapFinished state to false and resets the bootstrapCalled ref,
   * which triggers a re-evaluation of the component's dependencies and the bootstrap process.
   *
   * @returns {void}
   */
  const retryBootstrap = useCallback(() => {
    // Reset the bootstrapCalled ref so we can try bootstrapping again
    bootstrapCalled.current = false

    setState((prev) => ({
      ...prev,
      bootstrapFinished: false,
    }))
  }, [])

  const totalTaxSum = useMemo(() => {
    if (!state?.billingSchedule) {
      return 0
    }
    return state?.billingSchedule?.taxes?.reduce((acc, tax) => {
      return acc + Number(tax.tax_rate)
    }, 0)
  }, [state?.billingSchedule])

  return {
    ...state,
    retryBootstrap,
    totalTaxSum,
    validateSportsPayCard,
    tokenizeSportsPayCard,
    processSportsPayPayment,
  }
}

export default useSinglePaymentProvider
